import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import {
  NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet,
} from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { LoaderService } from '../core/services/loader.service';
import {
  trigger,
  transition,
  style,
  animate,
  query,
  group,
} from '@angular/animations';

export const routeSlideAnimation = trigger('routeSlide', [

  // List → Create/Edit : slide left (new page enters from right)
  transition('list => form', [
    group([
      query(':leave', [
        style({ position: 'absolute', width: '100%', opacity: 1, transform: 'translateX(0)' }),
        animate('280ms cubic-bezier(0.4,0,0.2,1)',
          style({ opacity: 0, transform: 'translateX(-40px)' })),
      ], { optional: true }),
      query(':enter', [
        style({ position: 'absolute', width: '100%', opacity: 0, transform: 'translateX(60px)' }),
        animate('320ms cubic-bezier(0.22,1,0.36,1)',
          style({ opacity: 1, transform: 'translateX(0)' })),
      ], { optional: true }),
    ]),
  ]),

  // Create/Edit → List : slide right (list returns from left)
  transition('form => list', [
    group([
      query(':leave', [
        style({ position: 'absolute', width: '100%', opacity: 1, transform: 'translateX(0)' }),
        animate('280ms cubic-bezier(0.4,0,0.2,1)',
          style({ opacity: 0, transform: 'translateX(60px)' })),
      ], { optional: true }),
      query(':enter', [
        style({ position: 'absolute', width: '100%', opacity: 0, transform: 'translateX(-40px)' }),
        animate('320ms cubic-bezier(0.22,1,0.36,1)',
          style({ opacity: 1, transform: 'translateX(0)' })),
      ], { optional: true }),
    ]),
  ]),
]);

@Component({
  standalone: false,
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  animations: [routeSlideAnimation],
})
export class LayoutComponent implements AfterViewInit, OnDestroy {
  sidebarCollapsed = false;
  mobileMenuOpen = false;
  isMobile = window.innerWidth <= 768;

  /** The scrollable content region — reset to the top on every navigation. */
  @ViewChild('contentArea') private contentArea?: ElementRef<HTMLElement>;

  private navSub?: Subscription;
  private navLoaderSub?: Subscription;

  // Tracks whether a navigation currently has the loader shown, so each
  // show() is balanced by exactly one hide() (LoaderService is ref-counted).
  private navLoaderActive = false;

  constructor(
    private router: Router,
    private loader: LoaderService,
  ) {}

  ngAfterViewInit(): void {
    // Reset scroll to the top after EACH completed navigation. The scroll can
    // live on .content-area OR on an inner page container (many screens use
    // their own overflow:auto wrapper), so reset the content-area plus any
    // scrolled descendants. Deferred to the next tick so it runs after the
    // incoming view + route animation have painted.
    this.navSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => setTimeout(() => this.resetScroll(), 0));

    // Common loader while a menu/route is being selected. Reuses the global
    // LoaderService (same overlay as HTTP loading). NavigationStart shows it;
    // the terminal events (End/Cancel/Error) hide it. A boolean guard keeps the
    // ref-counted show()/hide() balanced if events ever fire out of order.
    this.navLoaderSub = this.router.events
      .pipe(filter(e =>
        e instanceof NavigationStart ||
        e instanceof NavigationEnd ||
        e instanceof NavigationCancel ||
        e instanceof NavigationError,
      ))
      .subscribe(e => {
        if (e instanceof NavigationStart) {
          if (!this.navLoaderActive) {
            this.navLoaderActive = true;
            this.loader.show();
          }
        } else if (this.navLoaderActive) {
          this.navLoaderActive = false;
          this.loader.hide();
        }
      });
  }

  ngOnDestroy(): void {
    this.navSub?.unsubscribe();
    this.navLoaderSub?.unsubscribe();
    // Safety: if we're torn down mid-navigation, release the loader count.
    if (this.navLoaderActive) {
      this.navLoaderActive = false;
      this.loader.hide();
    }
  }

  /** Scroll the content area and any inner scrollable containers back to the top. */
  private resetScroll(): void {
    const host = this.contentArea?.nativeElement;
    if (!host) return;
    host.scrollTop = 0;
    host.scrollLeft = 0;
    // Reset inner page containers that own their own scroll.
    host.querySelectorAll<HTMLElement>('*').forEach(el => {
      if (el.scrollTop > 0) el.scrollTop = 0;
    });
  }

  /**
   * Stable animation-state property for [@routeSlide].
   * Updated via (activate) on the router outlet so Angular always
   * sees a consistent value across both change-detection passes —
   * preventing NG0100 ExpressionChangedAfterItHasBeenCheckedError.
   */
  routeState = 'list';

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth <= 768;
    if (!this.isMobile) this.mobileMenuOpen = false;
  }

  onHeaderMenuToggle(): void {
    if (this.isMobile) {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    } else {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    }
  }

  onSidebarToggle(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  /**
   * Called from (activate) on the router outlet — fires after the new
   * component is fully instantiated and its route data is settled.
   * Reads data.animation from the outlet and stores it as a plain
   * property so the [@routeSlide] binding never changes mid-cycle.
   */
  onRouteActivate(outlet: RouterOutlet): void {
    this.routeState = outlet?.activatedRouteData?.['animation'] ?? 'list';
    // Scroll reset is handled on NavigationEnd (see ngAfterViewInit) so it runs
    // after the incoming view has painted.
  }
}
